<template>
    <v-card>
        <v-card-title class="headline">
            <span>Create New Invoice</span>
        </v-card-title>

        <v-card-text>
            <v-form ref="createForm" v-model="formValid">
                <h4 class="mb-4">Add New Item</h4>
                <v-col cols="12">
                    <v-autocomplete
                        v-model="newPurchase.supplier"
                        label="Supplier *"
                        :items="supplierNames"
                        :loading="loadingSuppliers"
                        :rules="[v => !!v || 'Supplier is required']"
                        :menu-props="{ maxHeight: '300px', offsetY: true }"
                        outlined
                        dense
                        required
                        clearable
                        prepend-icon="mdi-account-box-outline"
                    />
                </v-col>
                <v-row no-gutters>
                    <v-col cols="6" class="px-3">
                        <v-text-field
                            v-model="newPurchase.totalPrice"
                            label="Total Price *"
                            type="number"
                            :rules="[v => !!v || 'Total Price is required']"
                            outlined
                            dense
                            required
                        />
                    </v-col>
                    <v-col cols="6" class="px-3">
                        <v-menu
                            ref="dueDateMenu"
                            v-model="dueDateMenu"
                            :close-on-content-click="true"
                            transition="scale-transition"
                            offset-y
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="dueDateText"
                                    label="Due Date *"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    :rules="[
                                        v => !!v || 'Due Date is required',
                                    ]"
                                    outlined
                                    dense
                                    required
                                />
                            </template>
                            <v-date-picker
                                :value="newPurchase.dueDate"
                                no-title
                                @input="onDueDatePickerInput"
                            />
                        </v-menu>
                    </v-col>
                </v-row>
                <v-col cols="12">
                    <div class="d-flex align-center">
                        <span class="text-caption mr-1 font-weight-bold"
                            >ATTACH:</span
                        >

                        <div
                            v-if="selectedFiles.length > 0"
                            class="d-flex align-center mr-2 files-scroll-container"
                            @wheel="handleWheelScroll"
                        >
                            <div
                                v-for="(file, index) in selectedFiles"
                                :key="index"
                                class="d-flex align-center mr-1 file-item"
                            >
                                <span
                                    class="text-caption mr-1"
                                    style="font-size: 13px;"
                                    >{{ file.name }}</span
                                >
                                <v-btn
                                    icon
                                    x-small
                                    color="error"
                                    @click="removeFile(index)"
                                >
                                    <v-icon x-small>mdi-delete</v-icon>
                                </v-btn>
                            </div>
                        </div>

                        <v-btn
                            @click="$refs.fileInput.click()"
                            color="primary"
                            fab
                            x-small
                            elevation="0"
                        >
                            <v-icon x-small>mdi-plus</v-icon>
                        </v-btn>
                    </div>

                    <input
                        ref="fileInput"
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png"
                        @change="handleFileSelection"
                        style="display: none"
                    />

                    <div
                        v-if="!isFormComplete && selectedFiles.length === 0"
                        class="text-caption text-error mt-1"
                    >
                        Files are required
                    </div>
                </v-col>
            </v-form>

            <div v-if="isFormComplete" class="mt-6">
                <h4 class="mb-4">Preview Item</h4>
                <v-simple-table dense class="elevation-1">
                    <thead>
                        <tr>
                            <th class="text-left">SUPPLIER</th>
                            <th class="text-center">TOTAL PRICE</th>
                            <th class="text-center">DUE DATE</th>
                            <th class="text-left">FILE</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <p class="my-0" style="font-size: 12px;">
                                    {{ currentPreviewItem.supplier }}
                                </p>
                            </td>
                            <td class="text-center">
                                <p class="my-0" style="font-size: 12px;">
                                    {{ currentPreviewItem.totalPrice }}
                                </p>
                            </td>
                            <td class="text-center">
                                <p class="my-0" style="font-size: 12px;">
                                    {{ currentPreviewItem.dueDate }}
                                </p>
                            </td>
                            <td>
                                <div
                                    v-if="
                                        currentPreviewItem.file &&
                                            currentPreviewItem.file.length > 0
                                    "
                                >
                                    <p
                                        v-for="(file,
                                        index) in currentPreviewItem.file"
                                        :key="index"
                                        class="my-0"
                                        style="font-size: 11px;"
                                    >
                                        {{ file }}
                                    </p>
                                </div>
                                <p
                                    v-else
                                    class="my-0"
                                    style="font-size: 11px; color: #999;"
                                >
                                    No files
                                </p>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </div>
        </v-card-text>

        <v-card-actions class="px-6">
            <v-spacer></v-spacer>
            <v-btn small text @click="closeDialog" :disabled="createLoading">
                Cancel
            </v-btn>
            <v-btn
                small
                rounded
                color="primary"
                @click="createSinglePurchase"
                :loading="createLoading"
                :disabled="!isFormComplete"
            >
                Create Purchase
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { storage } from '@/services/firebase'
import moment from 'moment'
import { mapMutations } from 'vuex'
import API from '@/services/api'

export default {
    name: 'CreateNoOrderInvoice',
    props: {
        companyId: {
            type: String,
            required: true,
        },
    },
    data: () => ({
        formValid: false,
        createLoading: false,
        loadingSuppliers: false,
        supplierNames: [],
        newPurchase: {
            supplier: '',
            totalPrice: '',
            dueDate: null,
        },
        selectedFiles: [],
        dueDateMenu: false,
        dueDateText: '',
    }),
    computed: {
        isFormComplete() {
            return (
                this.newPurchase.supplier &&
                this.newPurchase.totalPrice &&
                this.newPurchase.dueDate &&
                this.selectedFiles.length > 0
            )
        },
        currentPreviewItem() {
            if (!this.isFormComplete) return { file: [] }
            return {
                supplier: this.newPurchase.supplier,
                totalPrice: this.newPurchase.totalPrice,
                dueDate: this.newPurchase.dueDate,
                file: this.selectedFiles.map(file => file.name),
            }
        },
    },
    async created() {
        await this.loadSuppliers()
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async loadSuppliers() {
            try {
                this.loadingSuppliers = true
                const suppliers = await API.getLiteSuppliers().catch(() =>
                    API.getSuppliers()
                )
                const names = (suppliers || [])
                    .map(supplier =>
                        typeof supplier === 'string'
                            ? supplier
                            : supplier && supplier.name
                    )
                    .filter(Boolean)
                this.supplierNames = [...new Set(names)].sort((a, b) =>
                    a.localeCompare(b)
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loadingSuppliers = false
            }
        },
        closeDialog() {
            this.$emit('closeDialog')
        },
        onDueDatePickerInput(value) {
            this.$set(this.newPurchase, 'dueDate', value)
            this.dueDateText = value || ''
            this.dueDateMenu = false
        },
        handleWheelScroll(event) {
            const container = event.currentTarget
            event.preventDefault()
            container.scrollLeft += event.deltaY
        },
        handleFileSelection(event) {
            const newFiles = Array.from(event.target.files)
            const usedNames = new Set(this.selectedFiles.map(f => f.name))
            const renamedFiles = newFiles.map(file => {
                const newFileName = this.uniqueFileName(file.name, usedNames)
                return new File([file], newFileName, {
                    type: file.type,
                    lastModified: file.lastModified,
                })
            })
            this.selectedFiles = [...this.selectedFiles, ...renamedFiles]
            event.target.value = ''
        },
        uniqueFileName(originalName, usedNames) {
            const lastDot = originalName.lastIndexOf('.')
            const extension =
                lastDot !== -1 ? originalName.substring(lastDot) : ''
            const base =
                lastDot !== -1
                    ? originalName.substring(0, lastDot)
                    : originalName

            for (let n = 0; n < 10000; n += 1) {
                const candidate =
                    n === 0
                        ? `${base}${extension}`
                        : `${base} (${n})${extension}`
                if (!usedNames.has(candidate)) {
                    usedNames.add(candidate)
                    return candidate
                }
            }
            const fallback = `${base} (${Date.now()})${extension}`
            usedNames.add(fallback)
            return fallback
        },
        removeFile(index) {
            this.selectedFiles.splice(index, 1)
        },
        async createSinglePurchase() {
            if (!this.isFormComplete) return

            try {
                this.createLoading = true

                const purchaseData = {
                    companyId: this.companyId,
                    supplier: this.newPurchase.supplier,
                    totalPrice: this.newPurchase.totalPrice,
                    dueDate: this.newPurchase.dueDate,
                    file: [],
                    invoiceDate: moment()
                        .format('YYYY-MM-DD')
                        .toString(),
                }

                const result = await API.createNoOrderPurchase(purchaseData)
                const documentId = result.id

                if (this.selectedFiles.length > 0) {
                    const uploadedFileNames = []
                    const path = `${this.companyId}/noOrderPurchases/${documentId}/files`
                    const storageRef = storage().ref(path)

                    for (const file of this.selectedFiles) {
                        const fileRef = storageRef.child(file.name)
                        const metadata = {
                            customMetadata: {
                                filename: file.name,
                            },
                        }
                        await fileRef.put(file, metadata)
                        uploadedFileNames.push(file.name)
                    }

                    await API.updateNoOrderPurchase({
                        documentId: documentId,
                        file: uploadedFileNames,
                    })
                }

                this.$emit('created')
                this.closeDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: `Error creating purchase: ${error.message}`,
                })
            } finally {
                this.createLoading = false
            }
        },
    },
}
</script>

<style scoped>
.files-scroll-container {
    max-width: 300px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: thin;
    scrollbar-color: #ddd transparent;
}

.files-scroll-container::-webkit-scrollbar {
    height: 3px;
}

.files-scroll-container::-webkit-scrollbar-track {
    background: transparent;
}

.files-scroll-container::-webkit-scrollbar-thumb {
    background-color: #ddd;
    border-radius: 2px;
}

.files-scroll-container::-webkit-scrollbar-thumb:hover {
    background-color: #bbb;
}

.file-item {
    flex-shrink: 0;
    white-space: nowrap;
}
</style>
